import { action, makeObservable, observable, reaction } from 'mobx';
import {
  DEFAULT_GAME_RESULT_VALUE,
  DEFAULT_MULTIPLIER_VALUE,
  DEFAULT_WIN_CHANCE_VALUE,
  LimboSoundsName,
  MULTIPLIER_LIMIT,
  MULTIPLIER_PRECISION,
  MULTIPLIER_STEP_VALUE,
  LIMBO_RTP,
  WIN_CHANCE_LIMIT,
  WIN_CHANCE_PRECISION,
  WIN_CHANCE_STEP_VALUE,
} from '../limboCore/constants';
import { parseNumberPrecisionByDoteCount } from '../../../core/helpers/parseNumberByPrecision';
import { isMobile } from '../../../core/helpers/isMobile';
import { soundService } from '../../../services/baseSoundService/soundService';
import { userSettingsStore } from '../../../store/userSettingsStore';
import { checkHashStore } from '../../../store/checkHashStore';
import { betHistoryStore } from '../../../store/betHistoryStore';
import {
  countOfIncrease,
  increaseIntervalHelper,
  resultSoundTimeOutHelper,
} from '../limboCore/helpers';
import { userInfoStore } from '../../../store/userInfoStore';
import { setTimeoutRAF } from '../../../core/helpers/setTimeoutRAF';
import { mathConfig, mathJS } from '../../../core/helpers/mathFormat';
import { isPG } from '../../../core/helpers/skinHelper';
import { freeBetStore } from '../../../store/freeBetStore';

class LimboBetStore {
  limits = MULTIPLIER_LIMIT;

  multiplier = DEFAULT_MULTIPLIER_VALUE;

  winChance = DEFAULT_WIN_CHANCE_VALUE;

  gameResult = DEFAULT_GAME_RESULT_VALUE;

  isBetsClosed = false;

  lastResults = [];

  roundIsWin = null;

  winAmount = null;

  winOdd = null;

  constructor() {
    makeObservable(this, {
      winOdd: observable,
      limits: observable,
      winAmount: observable,
      winChance: observable,
      multiplier: observable,
      gameResult: observable,
      roundIsWin: observable,
      isBetsClosed: observable,
      setRoundIsWin: observable,
      lastResults: observable.deep,
      setWinOdd: action.bound,
      setLimits: action.bound,
      setWinAmount: action.bound,
      setWinChance: action.bound,
      setMultiplier: action.bound,
      setGameResult: action.bound,
      setLastResults: action.bound,
      setIsBetsClosed: action.bound,
      updateResultOdd: action.bound,
      updateLastResults: action.bound,
      handleIncreaseWinChance: action.bound,
      handleIncreaseMultiplier: action.bound,
      updateResultSaltAndKey: action.bound,
    });

    reaction(
      () => this.isBetsClosed,
      (value) => {
        const { setIsAllowedChangeCurrency } = userInfoStore;
        setIsAllowedChangeCurrency(!value);
        if (freeBetStore.currentFreeBet && !freeBetStore.currentFreeBet.remainingCount && !value) {
          freeBetStore.setIsShowFreeBetPopup(true);
        }
      }
    );

    reaction(
      () => freeBetStore.currentFreeBet,
      (value, prev) => {
        if (value && value.minOdd && (!prev || prev.id !== value.id)) {
          this.setMultiplier(value.minOdd);
          this.setLimits({
            minBet: value.minOdd,
          });
        } else if (!value) {
          this.setMultiplier(DEFAULT_MULTIPLIER_VALUE);
          this.setLimits(MULTIPLIER_LIMIT);
        }
      }
    );
  }

  setWinOdd(odd) {
    this.winOdd = odd;
    clearTimeout(this.timeOutOdd);
    this.timeOutOdd = setTimeout(() => {
      this.winOdd = null;
    }, 1500);
  }

  setLimits(arg) {
    this.limits = {
      ...this.limits,
      ...arg,
    };
  }

  setWinAmount(amount) {
    this.winAmount = amount;
    clearTimeout(this.timeOutWinAmount);
    this.timeOutWinAmount = setTimeout(() => {
      this.winAmount = null;
    }, 2000);
  }

  updateResultSaltAndKey({ salt, key, hash, newHash, odd, id }) {
    checkHashStore.setLastRoundInfo({ salt, key, hash });
    checkHashStore.setNextRoundHash(newHash);
    checkHashStore.setLastRoundResult({ resultOdd: odd, id });
  }

  setRoundIsWin(isWin) {
    this.roundIsWin = isWin;
  }

  setLastResults(lastResults) {
    this.lastResults = lastResults.map((item, index) => ({ ...item, id: index }));
  }

  updateLastResults(lastResult) {
    const updatedLastResult = [...this.lastResults, lastResult];
    if (updatedLastResult.length > (isMobile() ? 3 : 5)) {
      updatedLastResult.splice(0, 1);
    }
    const lastEl = updatedLastResult.pop();
    this.setLastResults(updatedLastResult);
    setTimeout(() => {
      this.setLastResults([...updatedLastResult, lastEl]);
    }, 0);
  }

  setIsBetsClosed(bool) {
    this.isBetsClosed = bool;
  }

  setGameResult(odd, isFinished) {
    this.gameResult = odd && Number(odd);
    if (isFinished) {
      this.gameResult = odd && Number(odd).toFixed(2);
    }
  }

  updateResultOdd({ odd, resultModel, key, salt, hash, newHash, id, winAmount, winOdd }) {
    const increaseCount = countOfIncrease(odd);
    let step;
    let newOdd;
    this.setGameResult(1, true);
    const percent = 0.05;
    if (userSettingsStore.isSoundOn) {
      if (userSettingsStore.isTurboOn) {
        soundService.play(resultModel.isWin ? LimboSoundsName.winTurbo : LimboSoundsName.loseTurbo);
      } else {
        setTimeoutRAF(() => {
          if (resultModel.isWin) {
            soundService
              .play(odd <= 4 ? LimboSoundsName.shortWin : LimboSoundsName.longWin)
              .then(() => {});
          } else {
            soundService
              .play(odd <= 5 ? LimboSoundsName.shortLose : LimboSoundsName.longLose)
              .then(() => {});
          }
        }, resultSoundTimeOutHelper(odd) / increaseCount);
      }
    }
    if (!userSettingsStore.isTurboOn) {
      // const time = performance.now()
      const recursiveIncrease = () => {
        setTimeoutRAF(() => {
          if (+this.gameResult < odd) {
            step = (odd * percent).toFixed(3);
            if (+this.gameResult + +step >= odd) {
              this.updateRoundState(odd, resultModel, winAmount, winOdd);
              this.updateResultSaltAndKey({ salt, key, hash, newHash, odd, id });
              return null;
            }
            newOdd = (+this.gameResult || 1) + +step;
          } else {
            step = (this.gameResult * percent).toFixed(3);
            if (+this.gameResult - +step <= +odd) {
              this.updateRoundState(odd, resultModel, winAmount, winOdd);
              this.updateResultSaltAndKey({ salt, key, hash, newHash, odd, id });
              this.setIsBetsClosed(false);
              return null;
            }
            newOdd = (this.gameResult || 1) - +step;
          }
          if (+this.gameResult === null) {
            this.setGameResult(DEFAULT_GAME_RESULT_VALUE);
          }
          this.setGameResult(+newOdd.toFixed(2));
          recursiveIncrease();
          return null;
        }, increaseIntervalHelper(odd, resultModel.isWin) / increaseCount);
      };
      recursiveIncrease();
    } else {
      this.updateRoundState(odd, resultModel, winAmount, winOdd);
      this.updateResultSaltAndKey({ salt, key, hash, newHash, odd, id });
    }
  }

  updateRoundState(odd, resultModel, winAmount, winOdd) {
    this.setGameResult(odd, true);
    this.updateLastResults(resultModel);
    this.setRoundIsWin(resultModel.isWin);
    this.setIsBetsClosed(false);
    betHistoryStore.getLastBet();
    setTimeout(() => {
      userInfoStore.getBalance();
    }, 500);
    setTimeout(() => {
      this.setWinAmount(winAmount);
      this.setWinOdd(winOdd);
    }, 1000);
  }

  setWinChance = (winChance, isChanges) => {
    let newMultiplier = parseNumberPrecisionByDoteCount(
      100,
      +mathJS.format(LIMBO_RTP(isPG()) / +winChance, mathConfig),
      MULTIPLIER_PRECISION
    );
    const newWinChance = parseNumberPrecisionByDoteCount(
      100,
      +mathJS.format(LIMBO_RTP(isPG()) / +newMultiplier, mathConfig),
      WIN_CHANCE_PRECISION
    );
    if (isChanges) {
      this.winChance = winChance;
    } else if (newWinChance > WIN_CHANCE_LIMIT.maxBet) {
      this.winChance = WIN_CHANCE_LIMIT.maxBet;
    } else if (newWinChance < WIN_CHANCE_LIMIT.minBet) {
      this.winChance = WIN_CHANCE_LIMIT.minBet;
    } else {
      this.winChance = Number.isFinite(newWinChance) ? +newWinChance : 0;
    }
    if (newMultiplier < this.limits.minBet) {
      newMultiplier = this.limits.minBet;
    }
    if (newMultiplier > this.limits.maxBet) {
      newMultiplier = this.limits.maxBet;
    }
    this.multiplier = Number.isFinite(newMultiplier) ? +newMultiplier : 0;
  };

  setMultiplier(multiplier) {
    this.multiplier = multiplier;
    let newWinChance = parseNumberPrecisionByDoteCount(
      100,
      +mathJS.format(LIMBO_RTP(isPG()) / +multiplier, mathConfig),
      WIN_CHANCE_PRECISION
    );
    if (newWinChance < WIN_CHANCE_LIMIT.minBet) {
      newWinChance = WIN_CHANCE_LIMIT.minBet;
    }
    if (newWinChance > WIN_CHANCE_LIMIT.maxBet) {
      newWinChance = WIN_CHANCE_LIMIT.maxBet;
    }
    this.winChance = Number.isFinite(newWinChance) ? +newWinChance : 0;
  }

  handleIncreaseWinChance(isIncrease) {
    const nextWinChanceValue = +mathJS.format(
      +this.winChance + (isIncrease ? 1 : -1) * WIN_CHANCE_STEP_VALUE,
      mathConfig
    );
    if (nextWinChanceValue > WIN_CHANCE_LIMIT.maxBet) {
      this.setWinChance(WIN_CHANCE_LIMIT.maxBet);
    } else if (nextWinChanceValue < WIN_CHANCE_LIMIT.minBet) {
      this.setWinChance(WIN_CHANCE_LIMIT.minBet);
    } else {
      const newWinChance = parseNumberPrecisionByDoteCount(
        1,
        nextWinChanceValue,
        WIN_CHANCE_PRECISION
      );
      this.setWinChance(Number.isFinite(newWinChance) ? +newWinChance : 0);
    }
  }

  handleIncreaseMultiplier(isIncrease) {
    const nextMultiplierValue = +mathJS.format(
      +this.multiplier + (isIncrease ? 1 : -1) * MULTIPLIER_STEP_VALUE,
      mathConfig
    );
    if (nextMultiplierValue > this.limits.maxBet) {
      this.setMultiplier(this.limits.maxBet);
    } else if (nextMultiplierValue < this.limits.minBet) {
      this.setMultiplier(this.limits.minBet);
    } else {
      const newMultiplier = parseNumberPrecisionByDoteCount(
        1,
        nextMultiplierValue,
        MULTIPLIER_PRECISION
      );
      this.setMultiplier(Number.isFinite(newMultiplier) ? +newMultiplier : 0);
    }
  }

  resetStore() {
    this.multiplier = DEFAULT_MULTIPLIER_VALUE;
    this.winChance = DEFAULT_WIN_CHANCE_VALUE;
    this.gameResult = DEFAULT_GAME_RESULT_VALUE;
    this.isBetsClosed = false;
    this.lastResults = [];
    this.roundIsWin = null;
    this.winAmount = null;
    this.winOdd = null;
    this.maxWinLimit = null;
  }
}

export const limboBetStore = new LimboBetStore();
